import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';

export interface StepRoute {
  id: number;
  route: string;
  nextRoute?: string;
  prevRoute?: string;
}

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [MatButtonModule, MatIconModule],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss'
})
export class FooterComponent implements OnInit, OnDestroy {
  @Input() stepRoutes: StepRoute[] = [];
  @Input() showBackButton: boolean = true;
  @Input() showNextButton: boolean = true;
  @Input() backButtonText: string = $localize `Previous`;
  @Input() nextButtonText: string =  $localize `Next`;
  @Input() submitButtonText: string = $localize `Confirm`;
  @Input() isNextDisabled: boolean = false;
  @Input() validateAndNavigate?: (nextRoute: string) => boolean;
  @Input() onSubmit?: () => void;
  @Input() currentStepInfo?: (url: string) => void;
  @Input() shouldDisableBackButton?: (url: string) => boolean;

  canGoBack: boolean = false;
  isLastStep: boolean = false;
  currentStep: StepRoute | null = null;

  private routerSubscription: Subscription = new Subscription();

  constructor(private readonly router: Router) { }

  ngOnInit() {
    this.initializeFromRoute();
    this.subscribeToRouteChanges();
  }

  ngOnDestroy() {
    this.routerSubscription.unsubscribe();
  }

  private initializeFromRoute() {
    this.updateCurrentStep();
  }

  private subscribeToRouteChanges() {
    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateCurrentStep();
      });
  }

  private updateCurrentStep() {
    if (this.currentStepInfo) {
      this.currentStepInfo(this.router.url);
    }
    const currentRoute = this.router.url;
    this.currentStep = this.stepRoutes.find(step => currentRoute.includes(step.route)) || null;

    if (this.currentStep) {
      if (this.shouldDisableBackButton) {
        this.canGoBack = !!this.currentStep.prevRoute && !this.shouldDisableBackButton(currentRoute);
      } else {
        this.canGoBack = !!this.currentStep.prevRoute;
      }

      this.isLastStep = !this.currentStep.nextRoute;
    }
  }

  onBack() {
    if (this.canGoBack && this.currentStep?.prevRoute) {
      this.router.navigate([this.currentStep.prevRoute]);
    }
  }

  onNext() {
    if (this.isLastStep) {
      if (this.onSubmit) {
        this.onSubmit();
      }
    } else if (this.currentStep?.nextRoute) {
      if (this.validateAndNavigate) {
        this.validateAndNavigate(this.currentStep.nextRoute);
      } else {
        this.router.navigate([this.currentStep.nextRoute]);
      }
    }
  }

  getNextButtonText(): string {
    return this.isLastStep ? this.submitButtonText : this.nextButtonText;
  }
}